import { useState, useEffect } from 'react';
import { markAttachmentDownloaded, wasAttachmentDownloaded } from '../utils/downloadedAttachments';

/**
 * Manages the malware-warning toast shown when opening an attachment preview.
 *
 * - Toast is suppressed if the attachment has already been downloaded once
 *   (persisted via localStorage keyed by Matrix event ID, cleared on logout).
 * - Auto-dismisses after {@link durationMs} milliseconds once visible.
 * - Timer is skipped entirely when toast is already suppressed.
 * - `markDownloaded` persists the suppression so the toast will be hidden on
 *   the next open; it does NOT dismiss the toast early in the current session.
 *
 * @param {object|null} message   - Matrix event object (may be null during SSR / initial render)
 * @param {object}      [options]
 * @param {number}      [options.durationMs=6000] - Auto-dismiss delay in milliseconds
 * @returns {{ showMalwareToast: boolean, dismiss: () => void, markDownloaded: () => void }}
 */
const useMalwareToast = (message, { durationMs = 6000 } = {}) => {
  // Suppressed if this attachment has already been downloaded once.
  const [showMalwareToast, setShowMalwareToast] = useState(
    () => !wasAttachmentDownloaded(message?.getId()),
  );

  useEffect(() => {
    // No-op when toast is already suppressed — avoids a wasted timer.
    if (!showMalwareToast) return;
    const timerId = setTimeout(() => setShowMalwareToast(false), durationMs);
    return () => clearTimeout(timerId);
  }, [showMalwareToast, durationMs]);

  const dismiss = () => setShowMalwareToast(false);

  /** Persist suppression without dismissing the toast early. */
  const markDownloaded = () => markAttachmentDownloaded(message?.getId());

  return { showMalwareToast, dismiss, markDownloaded };
};

export default useMalwareToast;
